/* eslint-disable react/prop-types */
import { useEffect, useState } from 'react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import Checkbox from '@/components/check-box'
import { useFetchReferences } from '@/pages/hooks/use-rewards-class'
import PropTypes from 'prop-types'
import InputBlock from '../input-block'
import { formatLabel } from '@/utils/label-utils'

const CategoryForm = ({
  searchTermMCC = '',
  searchTermMID = '',
  searchTermCID = '',
  onCategoryDescriptionChange,
  onCheckboxChange,
  onSearchChangeMCC,
  onSearchChangeMID,
  onSearchChangeCID,
  formik,
}) => {
  const { values, errors, touched, setFieldValue } = formik
  const { data: references = {}, ...fetchState } = useFetchReferences({ page: 1, perPage: 1000, search: '' })
  const { mccList = [], merchantList = [], chainIdList = [] } = references

  // Reset selected items whenever formik.values change (when editing a new category)
  useEffect(() => {
    setSelectedMCCItems(values.mccList || [])
    setSelectedMIDItems(values.midList || [])
    setSelectedCIDItems(values.chainList || [])
  }, [values.mccList, values.midList, values.chainList])

  const [selectedMCCItems, setSelectedMCCItems] = useState([])
  const [selectedMIDItems, setSelectedMIDItems] = useState([])
  const [selectedCIDItems, setSelectedCIDItems] = useState([])

  useEffect(() => {
    setFieldValue('mccList', selectedMCCItems)
    setFieldValue('midList', selectedMIDItems)
    setFieldValue('chainList', selectedCIDItems)
  }, [selectedMCCItems, selectedMIDItems, selectedCIDItems, setFieldValue])

  // Function to filter items based on search term
  const filterValues = (values, keys, searchTerm) => {
    return values.filter(item =>
      keys.some(key => item[key] && item[key].toLowerCase().includes(searchTerm.toLowerCase()))
    )
  }

  // Filtering MCC, MID, and CID values based on the search terms
  const filteredMCCValues = filterValues(mccList?.data || [], ['mcc', 'value'], searchTermMCC)
  const filteredMIDValues = filterValues(merchantList?.data || [], ['mId', 'storeName'], searchTermMID)
  const filteredCIDValues = filterValues(chainIdList?.data || [], ['chainID', 'label'], searchTermCID)

  // Handle checkbox changes
  const handleCheckboxChange = (type, index) => {
    const setSelectedItems = {
      MCC: setSelectedMCCItems,
      MID: setSelectedMIDItems,
      CHAIN: setSelectedCIDItems,
    }
    const selectedItems = {
      MCC: selectedMCCItems,
      MID: selectedMIDItems,
      CHAIN: selectedCIDItems,
    }

    const updatedItems = selectedItems[type].includes(index)
      ? selectedItems[type].filter(item => item !== index)
      : [...selectedItems[type], index]

    setSelectedItems[type](updatedItems)

    setFieldValue(`${type.toLowerCase()}List`, updatedItems)

    if (onCheckboxChange) {
      onCheckboxChange(type, index)
    }
  }

  const RenderInputField = ({
    header,
    searchTerm,
    onSearchChange,
    filteredValues,
    selectedItems,
    value,
    label,
    infoText,
    formikLabel,
    type,
    list,
  }) => (
    <div className="ml-3 flex gap-8 mt-6 mb-10">
      <div className="w-[384px] mb-[-40px]">
        <Label htmlFor="mcc" className="text-[--gray-700] text-sm font-medium">
          {header}
        </Label>
        <div className="border border-gray-200 rounded-lg mt-1">
          <Input
            placeholder="Search"
            value={searchTerm}
            onChange={e => onSearchChange(e.target.value)}
            className="w-[90%] text-[--gray-500] font-normal text-base mx-5 my-4"
          />
          {/* Handle Loading and Error States */}
          {fetchState.isLoading ? (
            <div className="w-[384px]  pl-6 h-[200px]">Loading...</div>
          ) : fetchState.isError ? (
            <div className="w-[384px] pl-6 h-[200px]">
              <p>Error loading mcc: {fetchState.error.message}</p>
              <button onClick={fetchState.refetch}>Retry</button>
            </div>
          ) : (
            <ScrollArea className="w-[384px] overflow-y-auto relative px-5 py-0 sm:shadow-sm h-[200px]">
              {filteredValues.map(item => (
                <div key={item.id} className="flex flex-row mb-3">
                  <Checkbox
                    isChecked={selectedItems.includes(item[value])}
                    onClick={() => handleCheckboxChange(type, item[value])}
                  />
                  <p className="w-[312px] h-auto font-medium text-[--gray-700] mt-[-1px]">
                    {formatLabel(item[label])} - {type === 'MCC' ? 'MCC' : ''} {item[value]}
                  </p>
                </div>
              ))}
            </ScrollArea>
          )}
        </div>
        <p className="text-sm text-[--gray-600] font-normal mt-1 mb-2">{infoText}</p>
        {errors[formikLabel] && touched[formikLabel] && <p className="text-red-600 mb-4">{errors[formikLabel]}</p>}
      </div>
      <div className="mb-2">
        <Label htmlFor="selectedMCC" className="text-[--gray-700] text-sm font-medium">
          Selected {header}
        </Label>
        <ScrollArea className="w-[384px] overflow-y-auto relative p-3 border border-gray-200 bg-white rounded-lg sm:shadow-sm h-[274px] mt-1">
          {selectedItems.map(data => {
            const item = list?.data?.find(item => item[value] === data)
            return (
              item && (
                <p key={item[value]} className="w-[384px] h-[24px] mb-6 text-[--gray-500] font-normal text-base">
                  {formatLabel(item[label])} - {type === 'MCC' ? 'MCC' : ''} {item[value]}
                </p>
              )
            )
          })}
        </ScrollArea>
      </div>
    </div>
  )

  return (
    <div>
      <div className="ml-3 flex gap-8">
        <div className="w-[384px] mb-[-40px]">
          <Label htmlFor="categoryDescription" className="text-[--gray-700] text-sm font-medium">
            Category description*
          </Label>
          <InputBlock
            className="mt-1"
            id="description"
            placeholder="Category description"
            onChange={e => {
              const value = e.target.value
              formik.setFieldValue('description', value)
              onCategoryDescriptionChange(value)
            }}
            formik={formik}
          />
          <p className="text-sm text-[--gray-600] font-normal mt-1 mb-6">
            A short description. Limited to 50 characters.
          </p>
        </div>
      </div>

      <RenderInputField
        header="MCC"
        searchTerm={searchTermMCC}
        onSearchChange={onSearchChangeMCC}
        filteredValues={filteredMCCValues}
        selectedItems={selectedMCCItems}
        value="mcc"
        label="value"
        type="MCC"
        infoText="Merchant category code from Highnote."
        formikLabel="mccList"
        list={mccList}
      />

      <RenderInputField
        header="MID"
        searchTerm={searchTermMID}
        onSearchChange={onSearchChangeMID}
        filteredValues={filteredMIDValues}
        selectedItems={selectedMIDItems}
        value="mId"
        label="storeName"
        type="MID"
        infoText="Merchant ID from Spade."
        formikLabel="midList"
        list={merchantList}
      />

      <RenderInputField
        header="Chain ID"
        searchTerm={searchTermCID}
        onSearchChange={onSearchChangeCID}
        filteredValues={filteredCIDValues}
        selectedItems={selectedCIDItems}
        value="chainID"
        label="label"
        type="CHAIN"
        infoText="Client ID from Chains."
        formikLabel="chainList"
        list={chainIdList}
      />
    </div>
  )
}

CategoryForm.propTypes = {
  formik: PropTypes.object.isRequired,
  searchTermMCC: PropTypes.string,
  searchTermMID: PropTypes.string,
  searchTermCID: PropTypes.string,
  onCategoryDescriptionChange: PropTypes.func.isRequired,
  onCheckboxChange: PropTypes.func,
  onSearchChangeMCC: PropTypes.func,
  onSearchChangeMID: PropTypes.func,
  onSearchChangeCID: PropTypes.func,
}

export default CategoryForm
